import React, {
  useState,
  ComponentType,
  useEffect,
  useCallback,
  useRef
} from 'react';
import {
  LodgeNewEnqiryTypes,
  MapLocationFormDataType,
  JobDateSelectionFormDataType,
  SelectAssetsOwnersFormDataType,
  UpdateUserProfileFormDataType,
  APP_URL
} from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Tile,
  ToastNotification,
  Loading,
  SkeletonText
} from 'carbon-components-react';
import { withFormik } from 'formik';
import {
  LodgeEnquiryForm1Schema,
  LodgeEnquiryForm2Schema,
  LodgeEnquiryForm3Schema,
  getLodgeEnquiryForm4Schema
} from 'utilities/validation';
import {
  JobDateSelectionForm,
  MapLocationForm,
  SelectAssetsOwnersForm,
  UpdateUserProfileForm
} from './forms';
import Utils from 'utilities/helper.util';
import {
  fetchLodgeEnquiryRequest,
  goToEnquiryPresets,
  selectNewEnquiryDetails,
  selectEnquiryJobDetails,
  selectIsSubmitting,
  selectErrorMessage,
  selectLastEnquiry,
  selectAddress,
  selectShape,
  selectPrePopulatedData,
  prePopulateEnquiryData,
  selectChannelTestMode
} from 'redux/lodgeEnquiry';
import { currentUser } from 'redux/session/selectors';
import { useComponentMount } from 'utilities/hooks.util';
import { selectedEnquiry, selectEnquiryLoader } from 'redux/enquiries';
import { RootState } from 'redux/root.reducer';
import errorJson from 'locales/en.json';

const getRecaptchaEnterpriseSiteKey = (): string =>
  process.env.REACT_APP_GOOGLE_RECAPTCHA_V3_KEY || '';

/**
 * Execute reCAPTCHA Enterprise (score-based) for lodge enquiry.
 *
 * WHY: Lodge enquiry is a high-risk submission flow and must include a
 * short-lived verification token (`recaptchaToken`) on final submit.
 *
 * HOW: The Enterprise script is injected at route-level by `AppRoutes.tsx`.
 * We call `grecaptcha.enterprise.ready()` then `execute(siteKey, { action })`.
 */
const executeRecaptcha = (action: string = 'submit'): Promise<string> => {
  return new Promise((resolve, reject) => {
    const siteKey = getRecaptchaEnterpriseSiteKey();
    if (!siteKey) {
      reject(new Error('Missing REACT_APP_GOOGLE_RECAPTCHA_V3_KEY'));
      return;
    }

    const w: any = window;
    if (!w.grecaptcha?.enterprise) {
      reject(new Error('grecaptcha.enterprise not available'));
      return;
    }

    w.grecaptcha.enterprise.ready(() => {
      w.grecaptcha.enterprise
        .execute(siteKey, { action })
        .then((token: string) => resolve(token))
        .catch(reject);
    });
  });
};

const isAppLoadedInFrame = window.self !== window.top;

const defaultForm1Values = {
  addressLine1: '',
  locality: '',
  state: '',
  postcode: '',
  locationTypes: [],
  locationsInRoad: [],
  rrLocationType: false,
  prLocationType: false
};
const defaultForm2Values = {
  digStartAt: '',
  digEndAt: '',
  autoRenewUntil: '',
  enquiryType: '',
  autoRenewToggle: false,
  activityTypes: '',
  isPlanningJob: null
};

const defaultForm3Values = {
  userReference: '',
  notes: '',
  authorityId: '',
  otherAuthorityName: ''
};

const defaultForm4Values = {
  phoneNumber: '',
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  locality: '',
  state: '',
  postcode: ''
};

interface Props {
  formRefs: React.MutableRefObject<any>;
  cancelEnquiryHandler: Function;
  completeEnquiryHandler: Function;
  hasCloneActivity: boolean;
  canAutoPresets: boolean;
}
export const LodgeNewEnquiryPanel: React.FC<Props> = ({
  hasCloneActivity,
  canAutoPresets,
  formRefs,
  cancelEnquiryHandler,
  completeEnquiryHandler
}: any) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const shape: any = useSelector(selectShape);
  const enquiryDetails: any = useSelector(selectNewEnquiryDetails);
  const jobDetails = useSelector(selectEnquiryJobDetails);
  const errors = useSelector(selectErrorMessage);
  const enquirySubmitting = useSelector(selectIsSubmitting);
  const selectedEnquiryAddress: any = useSelector(selectAddress);
  const user: any = useSelector(currentUser);
  const selectLastEnquiryValues: any = useSelector(selectLastEnquiry);
  const enquiryLoader: boolean = useSelector(selectEnquiryLoader);
  const enquiry: any = useSelector(selectedEnquiry);
  const mapGeocode = useSelector((state: RootState) => state.mapCache.geocode);
  const channelTestMode = useSelector(selectChannelTestMode);
  const prePopulatedData = useSelector(selectPrePopulatedData);

  const cloneFormInitializedRef = useRef(false);

  const { phoneNumber, Address } = user;
  const updateFormRefsValues = useCallback(
    (values: any, formId: number) => {
      if (formRefs?.current) {
        formRefs.current[`form${formId}`] = values;
      }
    },
    [formRefs]
  );

  const [enquiryPresets, setEnquiryPresets] = useState<any>();
  const [enquiryAddress, setEnquiryAddress] = useState<any>(
    selectedEnquiryAddress
  );
  const [activeStep, setActiveStep] = useState(0);
  const steps = !(Address && Address?.line1 && phoneNumber)
    ? [
        'Map Location',
        'Job Date selection',
        'Assets owner selection',
        'Update user profile'
      ]
    : ['Map Location', 'Job Date selection', 'Assets owner selection'];

  // FORM-1 iNITIAL VALUES
  const [form1Values, setForm1Values] = useState<MapLocationFormDataType>(
    defaultForm1Values
  );
  // FORM-2 iNITIAL VALUES
  const [form2Values, setForm2Values] = useState<JobDateSelectionFormDataType>(
    defaultForm2Values
  );
  // FORM-3 iNITIAL VALUES
  const [
    form3Values,
    setForm3Values
  ] = useState<SelectAssetsOwnersFormDataType>(defaultForm3Values);
  // FORM-4 iNITIAL VALUES
  const [form4Values, setForm4Values] = useState<UpdateUserProfileFormDataType>(
    defaultForm4Values
  );
  // FORM-ALL iNITIAL VALUES
  const [formValues, setFormValues] = useState<LodgeNewEnqiryTypes>({
    userReference: '',
    digStartAt: '',
    digEndAt: '',
    autoRenewUntil: '',
    notes: '',
    phoneNumber: null,
    Address: undefined,
    locationTypes: [],
    isPlanningJob: false,
    enquiryType: '',
    authorityId: '',
    locationsInRoad: [],
    activityTypes: '',
    shape: shape || enquiry?.shape
  });

  const [isFullPageLoader, setIsFullPageLoader] = useState<boolean>(false);
  const [isPanelLoader, setIsPanelLoader] = useState<boolean>(false);

  const autoSubmited = useRef<boolean>(false);

  const goToPreviousForm = () => {
    setActiveStep(activeStep - 1);
  };

  const updateFormHandler = useCallback(
    (enquiryData: any, option: any = {}) => {
      const {
        digStartAt,
        digEndAt,
        autoRenewUntil,
        locationTypes,
        locationsInRoad,
        activityTypes,
        isPlanningJob,
        userReference,
        addressLine1,
        notes,
        otherAuthorityName,
        authorityId
      } = enquiryData;

      const { overrideAutoRenewToggle = false } = option;

      const _formValues = [
        {
          ...form1Values,
          addressLine1,
          locationTypes,
          locationsInRoad,
          rrLocationType:
            Array.isArray(locationTypes) &&
            locationTypes.includes('Road Reserve'),
          prLocationType:
            Array.isArray(locationTypes) && locationTypes.includes('Private')
        },
        {
          ...form2Values,
          digStartAt: Utils.isAfterTodayDate(digStartAt)
            ? digStartAt
            : form2Values.digStartAt,
          digEndAt: Utils.isAfterTodayDate(digEndAt)
            ? digEndAt
            : form2Values.digEndAt,
          autoRenewUntil,
          autoRenewToggle: overrideAutoRenewToggle
            ? !!autoRenewUntil
            : undefined,
          activityTypes:
            Array.isArray(activityTypes) && activityTypes?.length > 0
              ? activityTypes.join(',')
              : '',
          isPlanningJob,
          enquiryType:
            isPlanningJob === null
              ? ''
              : isPlanningJob === true
              ? 'Design'
              : 'Excavation'
        },
        {
          ...form3Values,
          userReference,
          notes,
          otherAuthorityName:
            authorityId === 'other' && otherAuthorityName
              ? otherAuthorityName
              : '',
          authorityId: authorityId
            ? authorityId.toString()
            : otherAuthorityName
            ? 'other'
            : ''
        }
      ];

      return _formValues.map((_formVal: any, index: number) => {
        const indx = index + 1;
        indx === 1
          ? setForm1Values(_formVal)
          : indx === 2
          ? setForm2Values(_formVal)
          : setForm3Values(_formVal);
        updateFormRefsValues(_formVal, indx);
        return _formVal;
      });
    },
    [form1Values, form2Values, form3Values, updateFormRefsValues]
  );

  const handleHoEnquiryPresetsPage = () => {
    dispatch(goToEnquiryPresets(formRefs?.current));
  };

  const renderNewEnquiryStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <MapLocationFormComponent
            updateValues={(values: any) => updateFormRefsValues(values, 1)}
            cancelEnquiryHandler={() => cancelEnquiryHandler(false)}
            copyLastEnquiryHandler={() =>
              updateFormHandler(selectLastEnquiryValues)
            }
            applyEnquiryPresetsHandler={() =>
              enquiryPresets && updateFormHandler(enquiryPresets)
            }
            goEnquiryPresetsPageHandler={handleHoEnquiryPresetsPage}
            resetFormHandler={() => {
              setForm1Values(defaultForm1Values);
              updateFormRefsValues(defaultForm1Values, 1);
              setForm2Values(defaultForm2Values);
              updateFormRefsValues(defaultForm2Values, 2);
              setForm3Values(defaultForm3Values);
              updateFormRefsValues(defaultForm3Values, 3);
              setForm4Values(defaultForm4Values);
              updateFormRefsValues(defaultForm4Values, 4);
            }}
          />
        );
      case 1:
        return (
          <JobDateSelectionFormComponent
            updateValues={(values: any) => updateFormRefsValues(values, 2)}
            cancelEnquiryHandler={() => cancelEnquiryHandler(false)}
            goToPreviousFormHandler={goToPreviousForm}
          />
        );
      case 2:
        return (
          <AssetsOwnersFormComponent
            updateValues={(values: any) => updateFormRefsValues(values, 3)}
            cancelEnquiryHandler={() => cancelEnquiryHandler(false)}
            goToPreviousFormHandler={goToPreviousForm}
          />
        );
      case 3:
        return (
          <UpdateUserProfileFormComponent
            isBYDAMember={
              user?.preferences?.user_type === 'BYDA Member' || false
            }
            needPhoneNumber={!phoneNumber}
            getAddressSuggestion={(values: any) => setForm4Values(values)}
            updateValues={(values: any) => updateFormRefsValues(values, 4)}
            cancelEnquiryHandler={() => cancelEnquiryHandler(false)}
            goToPreviousFormHandler={goToPreviousForm}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  useEffect(() => {
    if (Address !== null || phoneNumber !== null) {
      const values = {
        phoneNumber,
        companyName: user?.companyName || '',
        addressLine1: Address?.line1 || '',
        addressLine2: Address?.line2 || '',
        locality: Address?.locality || '',
        state: Address?.state || '',
        postcode: Address?.postcode || ''
      };
      setForm4Values(values);
    }
  }, [phoneNumber, Address, user]);

  useEffect(() => {
    if (enquiryDetails && !Utils.isObjectEmpty(enquiryDetails)) {
      if (isAppLoadedInFrame) {
        history.push(APP_URL.ENQUIRY_LODGED);
      } else {
        const job = enquiryDetails.id;
        history.push({
          pathname: `/enquiries/${job}`,
          state: { job }
        });
      }
    }
  }, [history, enquiryDetails, enquiryLoader]);

  useEffect(() => {
    setIsFullPageLoader(enquirySubmitting || enquiryLoader);
    if (!enquirySubmitting && jobDetails) {
      completeEnquiryHandler();
    }
  }, [enquirySubmitting, enquiryLoader, jobDetails, completeEnquiryHandler]);

  useEffect(() => {
    if (formRefs.current) {
      const { form1 } = formRefs.current;
      if (form1) {
        setForm1Values({
          ...form1,
          addressLine1: ''
        });
      }
    }
  }, [formRefs]);

  useEffect(() => {
    if (user?.preferences?.enquiryPresets) {
      setEnquiryPresets(user.preferences.enquiryPresets);
    }
  }, [user]);

  useComponentMount(() => {
    user?.preferences?.enquiryPresets?.alwaysStart &&
      canAutoPresets &&
      updateFormHandler(user.preferences.enquiryPresets);
    if (formRefs.current) {
      const { form2, form3, form4 } = formRefs.current;
      form2 && setForm2Values(form2);
      form3 && setForm3Values(form3);
      form4 && setForm4Values(form4);
    }
  });

  const updateFormValues = async (values: any, goTo?: number) => {
    const webLaunchRequestId = prePopulatedData
      ? prePopulatedData.webLaunchRequestId
      : undefined;
    const autoSubmit = goTo === steps.length;
    if (isLastStep || autoSubmit) {
      setIsFullPageLoader(true);

      // Acquire reCAPTCHA Enterprise token on final submit (no pre-init/caching).
      // If the token cannot be obtained, block submission.
      let recaptchaToken: string;
      try {
        recaptchaToken = await executeRecaptcha('submit');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        setIsFullPageLoader(false);
        return;
      }

      if (values?.user) {
        const enquiryPayload = channelTestMode
          ? {
              ...formValues,
              ...channelTestMode,
              ...values,
              webLaunchRequestId,
              recaptchaToken
            }
          : {
              ...formValues,
              ...values,
              webLaunchRequestId,
              recaptchaToken
            };
        dispatch(
          fetchLodgeEnquiryRequest({
            enquiryPayload,
            userPayload: values.user
          })
        );
      } else {
        // Address contain id which causing not saving address in DB
        const Address = values.Address || {
          line1: formValues.Address?.line1,
          line2: formValues.Address?.line2,
          locality: formValues.Address?.locality,
          postcode: formValues.Address?.postcode,
          state: formValues.Address?.state,
          country: formValues.Address?.country
        };
        const enquiryPayload = channelTestMode
          ? {
              ...formValues,
              ...channelTestMode,
              ...values,
              Address,
              webLaunchRequestId,
              recaptchaToken
            }
          : {
              ...formValues,
              ...values,
              Address,
              webLaunchRequestId,
              recaptchaToken
            };
        dispatch(
          fetchLodgeEnquiryRequest({ enquiryPayload, userPayload: null })
        );
        dispatch(prePopulateEnquiryData(undefined));
      }
    } else {
      setFormValues({ ...formValues, ...values });
      setActiveStep(goTo !== undefined ? goTo : activeStep + 1);
    }
  };

  useEffect(() => {
    if (!enquiry) {
      cloneFormInitializedRef.current = false;
      return;
    }

    if (hasCloneActivity) {
      if (enquiry?.authorityId === null) {
        if (enquiry?.otherAuthorityName !== '') {
          enquiry.authorityId = 'other';
        } else {
          enquiry.authorityId = 'private';
        }
      }
      if (enquiry?.Address?.line1 === enquiry?.userReference) {
        enquiry.userReference = undefined;
      }

      if (!cloneFormInitializedRef.current) {
        updateFormHandler(enquiry);
        cloneFormInitializedRef.current = true;
      }

      if (selectedEnquiryAddress?.line1) {
        setEnquiryAddress({ ...selectedEnquiryAddress });
        setForm1Values((prev) => ({
          ...prev,
          addressLine1: selectedEnquiryAddress.line1,
          locality: selectedEnquiryAddress.locality || prev.locality,
          state: selectedEnquiryAddress.state || prev.state,
          postcode: selectedEnquiryAddress.postcode || prev.postcode
        }));
      } else if (typeof mapGeocode === 'string' && enquiry?.Address) {
        setEnquiryAddress({ ...enquiry.Address, id: null });
      }
    } else {
      setEnquiryAddress(
        enquiry?.Address
          ? { ...enquiry?.Address, id: null }
          : selectedEnquiryAddress
      );
    }
    // eslint-disable-next-line
  }, [enquiry, hasCloneActivity, selectedEnquiryAddress, mapGeocode]);

  useEffect(() => {
    if (prePopulatedData) {
      setIsPanelLoader(!enquiryAddress);
      if (!enquiryAddress) {
        return;
      }

      if (!prePopulatedData.authorityId) {
        if (prePopulatedData.otherAuthorityName !== '') {
          prePopulatedData.authorityId = 'other';
        } else {
          prePopulatedData.authorityId = 'private';
        }
      }
      const [form1, form2, form3] = updateFormHandler(prePopulatedData, {
        overrideAutoRenewToggle: true
      });

      const Address = {
        ...enquiryAddress,
        line1: form1.addressLine1 || enquiryAddress.line1,
        locality: form1.locality || enquiryAddress.locality,
        state: form1.state || enquiryAddress.state,
        postcode: form1.postcode || enquiryAddress.postcode
      };
      const form1Values = {
        Address,
        locationTypes: form1.locationTypes,
        locationsInRoad: form1.locationsInRoad
      };
      const form2Values = {
        ...form2,
        activityTypes: form2.activityTypes.split(',')
      };
      const form3Values = {
        userReference: form3.userReference,
        notes: form3.notes,
        authorityId: form3.authorityId,
        otherAuthorityName: form3.otherAuthorityName
      };

      const formValues = { ...form1Values, ...form2Values, ...form3Values };
      if (
        !Address.line1 ||
        !Address.locality ||
        !Address.state ||
        !Address.postcode ||
        !Array.isArray(prePopulatedData.locationTypes) ||
        !Array.isArray(prePopulatedData.locationsInRoad) ||
        prePopulatedData.locationTypes.length === 0 ||
        prePopulatedData.locationsInRoad.length === 0
      ) {
        updateFormValues(formValues, 0);
      } else if (
        !prePopulatedData.digStartAt ||
        !prePopulatedData.digEndAt ||
        !Array.isArray(prePopulatedData.activityTypes) ||
        prePopulatedData.activityTypes.length === 0 ||
        prePopulatedData.isPlanningJob === null ||
        prePopulatedData.isPlanningJob === undefined
      ) {
        updateFormValues(formValues, 1);
      } else {
        if (prePopulatedData.isAutoSubmit) {
          if (!autoSubmited.current) {
            // Prevent auto submit multiple time
            updateFormValues(formValues, 3);
          }
          autoSubmited.current = true;
        } else {
          updateFormValues(formValues, 2);
        }
      }
    }
    // eslint-disable-next-line
  }, [prePopulatedData, enquiryAddress]);

  // MAP LOCATION SELECTION:: STEP - 1
  const MapLocationFormComponent: ComponentType<any> = withFormik<
    typeof formValues,
    MapLocationFormDataType
  >({
    validationSchema: LodgeEnquiryForm1Schema,
    mapPropsToValues: () => ({
      addressLine1: form1Values.addressLine1 || enquiryAddress?.line1,
      locality: form1Values.locality || enquiryAddress?.locality,
      state: form1Values.state || enquiryAddress?.state,
      postcode: form1Values.postcode || enquiryAddress?.postcode,
      locationTypes: form1Values.locationTypes,
      locationsInRoad: form1Values.locationsInRoad,
      rrLocationType: form1Values.rrLocationType,
      prLocationType: form1Values.prLocationType
    }),
    handleSubmit: (values: MapLocationFormDataType, { setSubmitting }: any) => {
      const {
        addressLine1,
        locality,
        state,
        postcode,
        locationTypes,
        locationsInRoad
      } = values;
      const Address = {
        ...enquiryAddress,
        line1: addressLine1 || enquiryAddress.line1,
        locality: locality || enquiryAddress.locality,
        state: state || enquiryAddress.state,
        postcode: postcode || enquiryAddress.postcode
      };
      setSubmitting(true);
      setForm1Values(values);
      updateFormValues({
        Address,
        locationTypes,
        locationsInRoad
      });
    },
    displayName: 'MapLocationForm'
  })(MapLocationForm);

  // JOB DATE SELECTION:: STEP - 2
  const JobDateSelectionFormComponent: ComponentType<any> = withFormik<
    typeof formValues,
    JobDateSelectionFormDataType
  >({
    validationSchema: LodgeEnquiryForm2Schema,
    mapPropsToValues: () => ({
      digStartAt: form2Values?.digStartAt,
      digEndAt: form2Values?.digEndAt,
      autoRenewUntil: form2Values?.autoRenewUntil,
      enquiryType: form2Values?.enquiryType,
      activityTypes: form2Values.activityTypes,
      autoRenewToggle: form2Values?.autoRenewToggle || false,
      isPlanningJob: form2Values.isPlanningJob
    }),
    handleSubmit: (
      values: JobDateSelectionFormDataType,
      { setSubmitting }: any
    ) => {
      const _formatedValues = {
        ...values,
        activityTypes: values.activityTypes.split(',')
      };
      setSubmitting(true);
      setForm2Values(values);
      updateFormValues(_formatedValues);
    },
    displayName: 'JobDateSelectionFormComponent'
  })(JobDateSelectionForm);

  // ASSET OWNERS SELECTION:: STEP - 3
  const AssetsOwnersFormComponent: ComponentType<any> = withFormik<
    typeof formValues,
    SelectAssetsOwnersFormDataType
  >({
    validationSchema: LodgeEnquiryForm3Schema,
    mapPropsToValues: () => ({
      userReference: form3Values.userReference || '',
      notes: form3Values.notes || '',
      authorityId: form3Values.authorityId || '',
      otherAuthorityName: form3Values.otherAuthorityName || ''
    }),
    handleSubmit: (
      values: SelectAssetsOwnersFormDataType,
      { setSubmitting }: any
    ) => {
      const { userReference, notes, authorityId, otherAuthorityName } = values;
      setSubmitting(true);
      setForm3Values(values);
      updateFormValues({
        userReference,
        notes,
        authorityId,
        otherAuthorityName
      });
    },
    displayName: 'SelectAssetsOwnersFormComponent'
  })(SelectAssetsOwnersForm);

  // UPDATE USER PROFILE __OPTONAL:: STEP - 4
  const UpdateUserProfileFormComponent: ComponentType<any> = withFormik<
    typeof formValues,
    UpdateUserProfileFormDataType
  >({
    validationSchema: getLodgeEnquiryForm4Schema(!phoneNumber),
    mapPropsToValues: () => ({
      phoneNumber: form4Values.phoneNumber,
      companyName: form4Values.companyName,
      addressLine1: form4Values.addressLine1,
      addressLine2: form4Values.addressLine2,
      locality: form4Values.locality,
      state: form4Values.state,
      postcode: form4Values.postcode
    }),
    handleSubmit: (
      values: UpdateUserProfileFormDataType,
      { setSubmitting }: any
    ) => {
      setSubmitting(true);
      setForm4Values(values);
      updateFormValues({
        user: {
          phoneNumber: values.phoneNumber,
          companyName: values.companyName,
          Address: {
            line1: values.addressLine1,
            line2: values.addressLine2,
            locality: values.locality,
            state: values.state,
            postcode: values.postcode
          }
        }
      });
    },
    displayName: 'UpdateUserProfileFormComponent'
  })(UpdateUserProfileForm);

  return (
    <React.Fragment>
      {isFullPageLoader ? (
        <Loading
          className="fullPage--loader"
          withOverlay={true}
          description="Active loading indicator"
        />
      ) : (
        <Tile
          id="lodgeEnquiry"
          data-testid="lodgeEnquiry"
          className="lodgeEnquiry-panel white--bgColor bx--col-xxl-4 bx--col-lg-3 bx--col-md-3"
        >
          <section className="lodgeEnquiry-panel-section form-container">
            {isPanelLoader ? (
              <div className="topHeader-loading">
                <SkeletonText />
              </div>
            ) : (
              <>
                <div className="topHeader">
                  <h4>
                    {user?.Address === null && activeStep === 3
                      ? 'One more thing …'
                      : 'About this enquiry'}
                  </h4>
                  <div>
                    <Button
                      size="small"
                      kind="ghost"
                      onClick={() => cancelEnquiryHandler(true)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
                {errors && (
                  <ToastNotification
                    lowContrast
                    caption=""
                    iconDescription="describes the close button"
                    subtitle={Utils.optimizeErrMsg(
                      errorJson.Lodge.errors,
                      'Lodge.errors',
                      errors
                    )}
                    timeout={0}
                    title="Enquiry Error"
                  />
                )}
                {renderNewEnquiryStepContent(activeStep)}
              </>
            )}
          </section>
        </Tile>
      )}
    </React.Fragment>
  );
};

LodgeNewEnquiryPanel.displayName = 'LodgeNewEnquiryPanel';

export default React.memo(LodgeNewEnquiryPanel);
